import type {
  ChatMessage,
  ConnectionState,
  Domain,
  EventType,
  GeoEvent,
  ServerMessage,
  Signal,
  TrackedObject,
  ViewportBounds,
  WireInsight,
  WireObject,
} from "@/types/api";
import { ALL_EVENT_TYPES } from "@/types/api";
import { normalizeSignal, setClockOffset } from "./normalize";

/**
 * WsClient — the EARLYWIRE realtime data layer.
 *
 * Design notes (hard-won in the prototype, preserved here):
 *  · The signal/event feed is GLOBAL and lives on its own socket — never gated
 *    by the map viewport. A desk zoomed into EMEA still receives a Taiwan critical.
 *  · Tracked objects are sharded across multiple sockets because the server caps
 *    each subscription snapshot at 5,000 objects. Maritime gets N longitude-band
 *    shards (≈25–30k vessels); aircraft gets one; satellites are lazy.
 *  · The object store is PERSISTENT. Snapshots/positions only add/refresh; the
 *    server's viewport-scoped `remove` frames are ignored (objects leaving a
 *    subscription view are not gone, just out of that slice). A staleness TTL
 *    retires last-known positions instead.
 *  · "Live" is declared the moment an AUTHENTICATED socket OPENS — not when the
 *    first snapshot arrives (which can lag across many sockets).
 *
 * Auth: this layer does NOT own credentials. It calls `getToken()` (provided by
 * the Firebase auth layer) for a fresh ID token on every (re)connect.
 */

export interface WsCallbacks {
  onConn?: (state: ConnectionState) => void;
  onSnapshot?: () => void;
  onSignal?: (s: Signal) => void;
  onRevise?: (s: Signal, info: { sevChanged: boolean }) => void;
  onEvent?: (e: GeoEvent) => void;
  /** Desk chat changed (new message or moderation removal) — read client.chat. */
  onChat?: (m: ChatMessage | null) => void;
}

export interface WsClientOptions {
  wsUrl: string;
  getToken: () => Promise<string>;
  viewport?: ViewportBounds;
  eventTypes?: EventType[];
  callbacks?: WsCallbacks;
}

const GLOBAL_VP: ViewportBounds = {
  minLat: -75,
  minLon: -170,
  maxLat: 80,
  maxLon: 179,
};

/** Longitude bands tuned to real traffic density (narrow over busy seas). */
const MARITIME_BANDS: Array<[number, number]> = [
  [-180, -95],
  [-95, -25],
  [-25, 4],
  [4, 12],
  [12, 30],
  [30, 90],
  [90, 180],
];

/** Last-known positions persist until this old (ms). */
const TTL: Record<Domain, number> = {
  aircraft: 6 * 60_000,
  maritime: 60 * 60_000,
  satellite: 10 * 60_000,
};

interface Shard {
  name: string;
  domains: Domain[];
  primary: boolean;
  viewport: ViewportBounds | null;
  ws: WebSocket | null;
  backoff: number;
  closedByUs: boolean;
  /** pending reconnect timer — guards against stacking retry loops */
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  subscribe: () => void;
  connect: () => Promise<void>;
}

export class WsClient {
  readonly objects: TrackedObject[] = [];
  readonly events: GeoEvent[] = [];
  readonly signals: Signal[] = [];
  readonly chat: ChatMessage[] = [];
  readonly meta = { truncated: false };
  /** bumped whenever positions change — the render layer watches this */
  posVersion = 0;
  /** server clock − browser clock, learned from snapshot.serverTime / hb.t (±RTT/2). */
  clockOffsetMs: number | null = null;
  /** ingress→THIS BROWSER latency of the most recent signal frame, clock-skew corrected. */
  lastE2eMs: number | null = null;

  private readonly objIndex = new Map<string, TrackedObject>();
  private readonly sigIndex = new Map<string, Signal>();
  private readonly sockets = new Map<string, Shard>();
  private readonly opts: WsClientOptions;
  private readonly cb: WsCallbacks;
  private readonly eventTypes: EventType[];

  private viewport: ViewportBounds;
  private alive = true;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private sweepTimer: ReturnType<typeof setInterval> | null = null;
  private bpsTimer: ReturnType<typeof setInterval> | null = null;

  private bytes = 0;
  private bps = 0;
  private totalBytes = 0;
  private startTime = Date.now();
  private lastSnap = 0;
  /** last frame of ANY kind on the primary socket — zombie detection after sleep */
  private lastPrimaryFrame = 0;
  private wakeHandler: (() => void) | null = null;

  constructor(opts: WsClientOptions) {
    this.opts = opts;
    this.cb = opts.callbacks ?? {};
    this.viewport = opts.viewport ?? GLOBAL_VP;
    // Subscribe to ALL event types (low-volume); the map filters by the user's
    // selection client-side, so toggling is instant with no re-subscribe.
    this.eventTypes = opts.eventTypes ?? ALL_EVENT_TYPES;
    this.bpsTimer = setInterval(() => {
      this.bps = this.bytes;
      this.bytes = 0;
    }, 1000);
  }

  /* ───────────── public API ───────────── */

  /** Send a desk-chat message on the primary socket. False = not connected (caller shows a hint). */
  sendChat(body: string): boolean {
    const primary = [...this.sockets.values()].find((s) => s.primary);
    if (!primary?.ws || primary.ws.readyState !== WebSocket.OPEN) return false;
    primary.ws.send(JSON.stringify({ type: "chat", body }));
    return true;
  }

  /** Merge REST history (GET /api/chat) into the live ring, deduped by id, time-ordered. */
  seedChat(msgs: ChatMessage[]): void {
    const seen = new Set(this.chat.map((m) => m.id));
    for (const m of msgs) if (!seen.has(m.id)) this.chat.push(m);
    this.chat.sort((a, b) => a.at - b.at);
    if (this.chat.length > 200) this.chat.splice(0, this.chat.length - 200);
    this.cb.onChat?.(null);
  }

  async start(): Promise<void> {
    const primary = this.makeSocket("primary", [], true); // signals + events, global
    this.makeSocket("aircraft", ["aircraft"], false);
    MARITIME_BANDS.forEach((b, i) =>
      this.makeSocket("maritime-" + i, ["maritime"], false, {
        minLat: -75,
        minLon: b[0],
        maxLat: 80,
        maxLon: b[1],
      }),
    );
    await primary.connect(); // resolves on socket OPEN
    let i = 0;
    this.sockets.forEach((s) => {
      if (!s.primary) setTimeout(() => s.connect().catch(() => {}), i++ * 120);
    });
    this.refreshTimer = setInterval(() => {
      this.setViewport(this.viewport);
      this.sockets.get("primary")?.subscribe();
    }, 20_000);
    this.sweepTimer = setInterval(() => this.sweep(), 30_000);

    // Laptop-sleep recovery: timers are frozen while asleep and the TCP sockets die without a
    // close event (zombies), so on wake the UI either blinked "Live" on a dead pipe or sat on
    // "Reconnecting…" forever (a failed reconnect used to abandon the loop). Any wake signal
    // now audits every shard immediately.
    this.wakeHandler = () => this.kickStale();
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.wakeHandler);
      window.addEventListener("focus", this.wakeHandler);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.wakeHandler);
    }
  }

  stop(): void {
    this.alive = false;
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.sweepTimer) clearInterval(this.sweepTimer);
    if (this.bpsTimer) clearInterval(this.bpsTimer);
    if (this.watchdog) clearTimeout(this.watchdog);
    if (this.wakeHandler) {
      if (typeof window !== "undefined") {
        window.removeEventListener("online", this.wakeHandler);
        window.removeEventListener("focus", this.wakeHandler);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", this.wakeHandler);
      }
      this.wakeHandler = null;
    }
    this.sockets.forEach((s) => {
      s.closedByUs = true;
      if (s.reconnectTimer) {
        clearTimeout(s.reconnectTimer);
        s.reconnectTimer = null;
      }
      try {
        s.ws?.close();
      } catch {
        /* ignore */
      }
    });
  }

  /** Reconnect loop that can never die: a failed attempt (token fetch during a dead network,
   *  refused socket) schedules the next one instead of silently giving up — that silent
   *  give-up was the "stuck on Reconnecting… after sleep" bug. */
  private scheduleReconnect(sock: Shard, immediate = false): void {
    if (!this.alive || sock.closedByUs || sock.reconnectTimer) return;
    const wait = immediate ? 50 : Math.min(10_000, sock.backoff) * (0.85 + Math.random() * 0.3);
    if (!immediate) sock.backoff = Math.min(10_000, sock.backoff * 2);
    sock.reconnectTimer = setTimeout(() => {
      sock.reconnectTimer = null;
      sock
        .connect()
        .then(() => {
          if (sock.primary) {
            this.cb.onConn?.("resynced");
            setTimeout(() => this.cb.onConn?.("live"), 2500);
          }
        })
        .catch(() => this.scheduleReconnect(sock));
    }, wait);
  }

  /** Wake-time audit: reconnect anything dead, and force-cycle a zombie primary (socket looks
   *  OPEN but no frame — not even the 30s heartbeat — for over 70s: the TCP pipe died in sleep
   *  and no close event is ever coming). Honest UI: the LED drops to "reconnecting" instantly
   *  instead of blinking Live on a dead pipe. */
  private kickStale(): void {
    if (!this.alive) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    const now = Date.now();
    this.sockets.forEach((s) => {
      if (s.closedByUs) return;
      const ws = s.ws;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        s.backoff = 1000;
        this.scheduleReconnect(s, true);
      } else if (
        s.primary &&
        ws.readyState === WebSocket.OPEN &&
        this.lastPrimaryFrame > 0 &&
        now - this.lastPrimaryFrame > 70_000
      ) {
        this.cb.onConn?.("reconnecting");
        try {
          ws.close(); // onclose → scheduleReconnect
        } catch {
          /* ignore */
        }
      }
    });
  }

  /** Toggle the satellite shard (created lazily; it has its own 5k budget). */
  ensureSatellites(on: boolean): void {
    const ex = this.sockets.get("satellite");
    if (on && !ex) {
      this.makeSocket("satellite", ["satellite"], false).connect().catch(() => {});
    } else if (!on && ex) {
      ex.closedByUs = true;
      try {
        ex.ws?.close();
      } catch {
        /* ignore */
      }
      this.sockets.delete("satellite");
      this.clearShard("satellite");
    }
  }

  /**
   * Re-grid the maritime shards over a new view so each refills its own 5k
   * budget with LOCAL density. Uses an adaptive equal-count split: dense waters
   * get narrow strips, open ocean gets wide ones. Self-rebalances each cycle.
   */
  setViewport(vp: ViewportBounds): void {
    this.viewport = vp;
    const shards = [...this.sockets.values()].filter((s) => s.name.startsWith("maritime-"));
    const n = shards.length;
    if (!n) return;
    const lons: number[] = [];
    for (const o of this.objects) {
      if (
        o.domain === "maritime" &&
        o.lon >= vp.minLon && o.lon <= vp.maxLon &&
        o.lat >= vp.minLat && o.lat <= vp.maxLat
      ) {
        lons.push(o.lon);
      }
    }
    let bounds: number[];
    if (lons.length > n * 40) {
      lons.sort((a, b) => a - b);
      bounds = [vp.minLon];
      for (let i = 1; i < n; i++) {
        let cut = lons[Math.floor((lons.length * i) / n)];
        cut = Math.max(cut, bounds[i - 1] + 0.25);
        cut = Math.min(cut, vp.maxLon - 0.25 * (n - i));
        bounds.push(cut);
      }
      bounds.push(vp.maxLon);
    } else {
      bounds = [];
      for (let i = 0; i <= n; i++) bounds.push(vp.minLon + ((vp.maxLon - vp.minLon) * i) / n);
    }
    shards.forEach((s, i) => {
      s.viewport = { minLon: bounds[i], maxLon: bounds[i + 1], minLat: vp.minLat, maxLat: vp.maxLat };
    });
    let j = 0;
    this.sockets.forEach((s) => {
      if (!s.primary) setTimeout(() => s.subscribe(), j++ * 100);
    });
  }

  avgKbps(): number {
    return this.totalBytes / 1000 / Math.max(1, (Date.now() - this.startTime) / 1000);
  }
  bytesPerSec(): number {
    return this.bps;
  }
  snapshotAge(): number | null {
    return this.lastSnap ? Math.round((Date.now() - this.lastSnap) / 1000) : null;
  }

  /* ───────────── internals ───────────── */

  private upsertObjects(arr: WireObject[], shard: string): void {
    const now = Date.now();
    for (const o of arr) {
      const ex = this.objIndex.get(o.id);
      if (ex) {
        ex.lat = +o.lat;
        ex.lon = +o.lon;
        ex.heading = o.heading;
        ex.speed = o.speed;
        ex.lastSeen = o.lastSeen;
        ex.shard = shard;
        ex.seenAt = now;
      } else {
        const n: TrackedObject = {
          id: o.id,
          domain: o.domain,
          lat: +o.lat,
          lon: +o.lon,
          heading: o.heading,
          speed: o.speed,
          name: o.name,
          lastSeen: o.lastSeen,
          shard,
          seenAt: now,
        };
        this.objects.push(n);
        this.objIndex.set(o.id, n);
      }
    }
    this.posVersion++;
  }

  private clearShard(name: string): void {
    for (let i = this.objects.length - 1; i >= 0; i--) {
      if (this.objects[i].shard === name) {
        this.objIndex.delete(this.objects[i].id);
        this.objects.splice(i, 1);
      }
    }
  }

  private sweep(): void {
    const now = Date.now();
    for (let i = this.objects.length - 1; i >= 0; i--) {
      const o = this.objects[i];
      if (now - (o.seenAt ?? 0) > (TTL[o.domain] ?? 30 * 60_000)) {
        this.objIndex.delete(o.id);
        this.objects.splice(i, 1);
      }
    }
  }

  private resetWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      this.cb.onConn?.("reconnecting");
      const p = this.sockets.get("primary");
      try {
        p?.ws?.close();
      } catch {
        /* ignore */
      }
    }, 60_000);
  }

  private handle(d: ServerMessage, sock: Shard): void {
    if (sock.primary) this.lastPrimaryFrame = Date.now();
    if (d.type === "hb") {
      if (typeof d.t === "number") {
        this.clockOffsetMs = d.t - Date.now();
        setClockOffset(this.clockOffsetMs);
      }
    }
    if (d.type === "snapshot") {
      this.lastSnap = Date.now();
      if (typeof d.serverTime === "number") {
        this.clockOffsetMs = d.serverTime - Date.now();
        setClockOffset(this.clockOffsetMs);
      }
      this.upsertObjects(d.objects ?? [], sock.name); // persistent: never clears
      if (sock.primary) {
        this.events.length = 0;
        (d.events ?? []).forEach((e) => this.events.push(e));
        this.signals.length = 0;
        this.sigIndex.clear();
        (d.insights ?? []).forEach((raw) => {
          const s = normalizeSignal(raw);
          this.signals.push(s);
          this.sigIndex.set(s.id, s);
        });
      }
      this.meta.truncated = this.meta.truncated || !!d.truncated;
      this.cb.onSnapshot?.();
    } else if (d.type === "positions") {
      this.upsertObjects(d.objects ?? [], sock.name);
    } else if (d.type === "remove") {
      // intentionally ignored — see class doc (persistent store)
    } else if (d.type === "signal" && sock.primary) {
      const s = normalizeSignal(d.signal as WireInsight);
      // The wire-freshness measurement the footer shows: engine ingress → this browser,
      // corrected by the learned server-clock offset so a skewed local clock can't lie.
      // FRESHNESS WINDOW: expiry sweeps re-broadcast OLD signals (resolutions of yesterday's
      // quakes) whose ingress is hours in the past — that's signal age, not pipeline latency,
      // and must not poison the meter. Only frames that traversed the pipe just now count.
      if (s.engineIngressAt && this.clockOffsetMs != null) {
        const e2e = Date.now() + this.clockOffsetMs - s.engineIngressAt;
        if (e2e >= 0 && e2e < 120_000) this.lastE2eMs = e2e;
      }
      const ex = this.sigIndex.get(s.id);
      if (ex) {
        const newer = (s.updatedAt ?? 0) > (ex.updatedAt ?? 0);
        const sevChanged = ex.severity !== s.severity;
        Object.assign(ex, s);
        if (newer) this.cb.onRevise?.(ex, { sevChanged });
      } else {
        this.signals.unshift(s);
        this.sigIndex.set(s.id, s);
        this.cb.onSignal?.(s);
      }
    } else if (d.type === "event" && sock.primary) {
      this.events.unshift(d.event);
      if (this.events.length > 300) this.events.pop();
      this.cb.onEvent?.(d.event);
    } else if (d.type === "chat" && sock.primary) {
      if (!this.chat.some((m) => m.id === d.msg.id)) {
        this.chat.push(d.msg);
        if (this.chat.length > 200) this.chat.shift();
      }
      this.cb.onChat?.(d.msg);
    } else if (d.type === "chat-remove" && sock.primary) {
      const i = this.chat.findIndex((m) => m.id === d.id);
      if (i >= 0) this.chat.splice(i, 1);
      this.cb.onChat?.(null);
    }
    if (sock.primary) this.resetWatchdog();
  }

  private makeSocket(name: string, domains: Domain[], primary: boolean, vp?: ViewportBounds): Shard {
    const sock: Shard = {
      name,
      domains,
      primary,
      viewport: vp ?? null,
      ws: null,
      backoff: 1000,
      closedByUs: false,
      reconnectTimer: null,
      subscribe: () => {
        if (sock.ws && sock.ws.readyState === WebSocket.OPEN) {
          sock.ws.send(
            JSON.stringify({
              type: "subscribe",
              viewport: primary ? GLOBAL_VP : sock.viewport ?? this.viewport,
              domains: sock.domains,
              eventTypes: primary ? this.eventTypes : [],
            }),
          );
        }
      },
      connect: () =>
        new Promise<void>((resolve, reject) => {
          // Never open a socket after the client is stopped (or this shard was
          // closed): leaked staggered-start / reconnect timers would otherwise
          // construct a real WebSocket post-teardown (also the source of the
          // jsdom cookie rejections under test).
          if (!this.alive || sock.closedByUs) {
            resolve();
            return;
          }
          // Already connected (e.g. a wake-kick raced a normal reconnect) — never double-open.
          if (sock.ws && (sock.ws.readyState === WebSocket.OPEN || sock.ws.readyState === WebSocket.CONNECTING)) {
            resolve();
            return;
          }
          let settled = false;
          this.opts
            .getToken()
            .then((token) => {
              // stop() may have fired while the token request was in flight.
              if (!this.alive || sock.closedByUs) {
                if (!settled) {
                  settled = true;
                  resolve();
                }
                return;
              }
              let ws: WebSocket;
              try {
                ws = new WebSocket(this.opts.wsUrl, ["latl.bearer", token]);
              } catch (e) {
                reject(e);
                return;
              }
              sock.ws = ws;
              ws.onopen = () => {
                sock.backoff = 1000;
                sock.subscribe();
                if (primary) {
                  this.resetWatchdog();
                  this.cb.onConn?.("live");
                }
                if (!settled) {
                  settled = true;
                  resolve();
                }
              };
              ws.onmessage = (m) => {
                const len = typeof m.data === "string" ? m.data.length : 0;
                this.bytes += len;
                this.totalBytes += len;
                let d: ServerMessage;
                try {
                  d = JSON.parse(m.data);
                } catch {
                  return;
                }
                this.handle(d, sock);
              };
              ws.onerror = () => {
                if (!settled) {
                  settled = true;
                  reject(new Error("ws error"));
                }
              };
              ws.onclose = () => {
                if (primary && this.watchdog) clearTimeout(this.watchdog);
                if (!this.alive || sock.closedByUs) return;
                if (primary) this.cb.onConn?.("reconnecting");
                this.scheduleReconnect(sock);
              };
            })
            .catch(reject);
        }),
    };
    this.sockets.set(name, sock);
    return sock;
  }
}
